import { useCallback, useEffect, useMemo, useState } from 'react';
import ListPageLayout from '../../components/ListPageLayout';
import { createPdfBlob } from '../../utils/PDFExporter';
import { openPdfInApp } from '../../utils/openPdfInApp';
import { buildCustomerLedgerPdfHeader } from '../../utils/customerLedgerPdf';
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { Button } from 'reactstrap';
import Select from 'react-select';
import { DatePicker } from 'antd';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { getAllPlants } from '../../services/PlantServices';
import { getAllCompanies } from '../../services/CompanyServices';
import { getAllSalesRepresentatives } from '../../services/SalesRepresentativeServices';
import { getMarketingPersonReport } from '../../services/ReportServices';
import { displayError } from '../../helpers';
import { HandleSelect, reportMultiSelectComponents, reportFilterSelectStyles } from '../../components/MultiSelectControls';
import ReportExportDropdown from '../../components/ReportExportDropdown';
import MarketingPersonReportSection from './MarketingPersonReportSection';
import {
  EMPTY_REPORT_DETAILS,
  MARKETING_REPORT_SECTIONS,
  filterMarketingReportRows,
  getMarketingSectionCount,
} from './marketingPersonReportSections';
import { buildListExcelExportHeader } from '../../utils/listExcelExportHeader';
import { exportMarketingPersonReportExcel } from './marketingPersonReportExport';

const { RangePicker } = DatePicker;
const filterSelectStyles = reportFilterSelectStyles;

function resolveMultiIds(selected, allOptions) {
  if (!selected || selected.length === 0) {
    return '*';
  }
  if (selected.some((item) => item.value === 'select-all')) {
    return allOptions.map((item) => item.value).join(',') || '*';
  }
  return selected.map((item) => item.value).join(',');
}

function formatAmount(value) {
  return Number(value || 0).toFixed(2);
}

function resolvePlantIds(selected, allOptions) {
  if (!allOptions.length) {
    return [];
  }
  if (!selected || selected.length === 0) {
    return allOptions.map((item) => item.value);
  }
  if (selected.some((item) => item.value === 'select-all')) {
    return allOptions.map((item) => item.value);
  }
  return selected.map((item) => item.value);
}

function buildMarketingReportInfoLines({
  fromDate,
  toDate,
  asOnDate,
  marketingPersonLabel,
  extraLines = [],
}) {
  const lines = [
    `Period: ${fromDate.format('DD-MM-YYYY')} to ${toDate.format('DD-MM-YYYY')}`,
    `Outstanding as on: ${asOnDate.format('DD-MM-YYYY')}`,
    `Marketing Person: ${marketingPersonLabel}`,
  ];
  extraLines.filter(Boolean).forEach((line) => lines.push(line));
  return lines;
}

function MarketingPersonReport() {
  const [cookies] = useCookies(['myToken']);
  const [totals, setTotals] = useState(null);
  const [reportDetails, setReportDetails] = useState(EMPTY_REPORT_DETAILS);
  const [plants, setPlants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [salesreps, setSalesreps] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState([]);
  const [selectedSalesrep, setSelectedSalesrep] = useState([]);
  const [fromDate, setFromDate] = useState(moment().startOf('month'));
  const [toDate, setToDate] = useState(moment());
  const [asOnDate, setAsOnDate] = useState(moment());
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);
  const [activeSectionKey, setActiveSectionKey] = useState(MARKETING_REPORT_SECTIONS[0].key);

  const companiesWithSelectAll = useMemo(
    () => [{ value: 'select-all', label: 'Select All companies' }, ...companies],
    [companies],
  );
  const plantsWithSelectAll = useMemo(
    () => [{ value: 'select-all', label: 'Select All plants' }, ...plants],
    [plants],
  );
  const salesrepsWithSelectAll = useMemo(
    () => [{ value: 'select-all', label: 'Select All sales reps' }, ...salesreps],
    [salesreps],
  );

  useEffect(() => {
    getAllCompanies(cookies).then((companyList) => {
      setCompanies((companyList.company_data || [])
        .filter((company) => company.status === true || company.status === 1 || company.status === 'Active')
        .map((company) => ({
          value: company.id,
          label: company.alias || company.entity_name,
        })));
    });
    getAllPlants(cookies).then((plantList) => {
      setPlants((plantList.plant_list || [])
        .filter((plant) => plant.status)
        .map((plant) => ({ value: plant.id, label: plant.plant_alias })));
    });
    getAllSalesRepresentatives(cookies).then((salesRepList) => {
      setSalesreps((salesRepList.salesrep_data || [])
        .filter((salesrep) => salesrep.status === true || salesrep.status === 1 || salesrep.status === 'Active')
        .map((salesrep) => ({ value: salesrep.id, label: salesrep.entity_name })));
    });
  }, [cookies]);

  const filteredReportDetails = useMemo(() => {
    const next = { ...reportDetails };
    MARKETING_REPORT_SECTIONS.forEach((section) => {
      if (section.type === 'count') {
        return;
      }
      next[section.key] = filterMarketingReportRows(
        reportDetails[section.key],
        section.columns,
        searchText,
      );
    });
    return next;
  }, [reportDetails, searchText]);

  const activeSection = useMemo(
    () => MARKETING_REPORT_SECTIONS.find((section) => section.key === activeSectionKey)
      || MARKETING_REPORT_SECTIONS[0],
    [activeSectionKey],
  );

  const marketingPersonLabel = useMemo(() => {
    if (!selectedSalesrep.length) {
      return 'All marketing persons';
    }
    if (selectedSalesrep.some((item) => item.value === 'select-all')) {
      return 'All marketing persons';
    }
    return selectedSalesrep.map((item) => item.label).join(', ');
  }, [selectedSalesrep]);

  const activeSectionCount = useMemo(() => (
    getMarketingSectionCount(activeSection, filteredReportDetails)
  ), [activeSection, filteredReportDetails]);

  const exportFileBase = useMemo(() => {
    const datePart = fromDate?.isValid() && toDate?.isValid()
      ? `${fromDate.format('DD-MM-YYYY')}_to_${toDate.format('DD-MM-YYYY')}`
      : moment().format('DD-MM-YYYY');
    return `MarketingPersonReport_${datePart}`;
  }, [fromDate, toDate]);

  const pdfSummaryLines = useMemo(() => {
    if (!fromDate?.isValid() || !toDate?.isValid()) {
      return [];
    }
    const lines = [`Period: ${fromDate.format('DD-MM-YYYY')} to ${toDate.format('DD-MM-YYYY')}`];
    if (asOnDate?.isValid()) {
      lines.push(`Balance as on: ${asOnDate.format('DD-MM-YYYY')}`);
    }
    return lines;
  }, [asOnDate, fromDate, toDate]);

  const summaryCards = useMemo(() => {
    if (!totals) {
      return [];
    }
    return [
      { label: 'Existing Customers', value: String(reportDetails.existing_customer_count || 0) },
      { label: 'New Customers', value: String(totals.new_customers || 0) },
      { label: 'Quotations', value: String(totals.quotation_count || 0) },
      { label: 'Sales Amount', value: formatAmount(totals.sales_amount) },
      { label: 'Collections', value: formatAmount(totals.collection_amount) },
      { label: 'Outstanding', value: formatAmount(totals.balance_amount) },
    ];
  }, [reportDetails.existing_customer_count, totals]);

  const loadReport = useCallback(() => {
    if (!fromDate?.isValid() || !toDate?.isValid()) {
      displayError({ message: 'Please select a valid date range.' }, 'Report Failed');
      return Promise.resolve();
    }
    if (!asOnDate?.isValid()) {
      displayError({ message: 'Please select a valid balance as-on date.' }, 'Report Failed');
      return Promise.resolve();
    }

    setIsLoading(true);
    const params = {
      company_id: resolveMultiIds(selectedCompany, companies),
      plant_id: resolveMultiIds(selectedPlant, plants),
      salesrep_ids: resolveMultiIds(selectedSalesrep, salesreps),
      from_date: fromDate.format('DD-MM-YYYY'),
      upto_date: toDate.format('DD-MM-YYYY'),
      as_on_date: asOnDate.format('DD-MM-YYYY'),
    };

    return getMarketingPersonReport(cookies, params)
      .then((response) => {
        setTotals(response.totals || null);
        setReportDetails({
          existing_customer_count: response.existing_customer_count ?? 0,
          new_customer_list: response.new_customer_list || [],
          quotation_list: response.quotation_list || [],
          sales_order_list: response.sales_order_list || [],
          sales_list: response.sales_list || [],
          payment_receipt_list: response.payment_receipt_list || [],
          outstanding_list: response.outstanding_list || [],
        });
        setActiveSectionKey(MARKETING_REPORT_SECTIONS[0].key);
        setHasGenerated(true);
      })
      .catch((error) => {
        displayError(error?.response?.data || error, 'Report Failed');
        setTotals(null);
        setReportDetails(EMPTY_REPORT_DETAILS);
      })
      .finally(() => setIsLoading(false));
  }, [asOnDate, cookies, companies, fromDate, plants, salesreps, selectedCompany, selectedPlant, selectedSalesrep, toDate]);

  const ensureExportData = () => {
    if (!hasGenerated) {
      displayError({ message: 'Generate the report first, then export.' }, 'Export Failed');
      return false;
    }
    return true;
  };

  const handleExcelExport = async () => {
    if (!ensureExportData()) return;
    setIsLoading(true);
    try {
      const plantIds = resolvePlantIds(selectedPlant, plants);
      const reportInfoLines = buildMarketingReportInfoLines({
        fromDate,
        toDate,
        asOnDate,
        marketingPersonLabel,
      });
      const exportHeader = await buildListExcelExportHeader(cookies, plantIds, {
        documentTitle: 'Marketing Person Report',
        extraInfoLines: reportInfoLines,
      });
      await exportMarketingPersonReportExcel({
        filename: exportFileBase,
        reportDetails: filteredReportDetails,
        documentTitle: 'Marketing Person Report',
        infoLines: exportHeader.infoLines,
        header: {
          companyName: exportHeader.companyName,
          addressLines: exportHeader.addressLines,
          gstNo: exportHeader.gstNo,
          logoDataUrl: exportHeader.logoDataUrl,
        },
      });
    } catch (error) {
      displayError({ message: error?.message || 'Excel export failed.' }, 'Export Failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePDFExport = async () => {
    if (!ensureExportData()) return;
    setIsLoading(true);
    try {
      const header = await buildCustomerLedgerPdfHeader(cookies, [], [], {
        documentTitle: 'Marketing Person Report',
        subtitle: pdfSummaryLines.join(' | '),
      });
      const pdfColumns = MARKETING_REPORT_SECTIONS
        .filter((section) => section.type !== 'count')
        .flatMap((section) => section.columns.map((column) => ({
          header: `${section.title} - ${column.label}`,
          dataKey: column.key,
          align: column.align,
        })));
      const combinedRows = MARKETING_REPORT_SECTIONS
        .filter((section) => section.type !== 'count')
        .flatMap((section) => (filteredReportDetails[section.key] || []).map((row) => ({
          ...row,
          section_title: section.title,
        })));
      const blob = createPdfBlob('', pdfColumns, combinedRows, pdfSummaryLines, header);
      openPdfInApp(blob, {
        title: 'Marketing Person Report',
        downloadFileName: `${exportFileBase}.pdf`,
      });
    } catch (error) {
      displayError({ message: error?.message || 'PDF export failed.' }, 'Export Failed');
    } finally {
      setIsLoading(false);
    }
  };

  const exportButtons = (
    <ReportExportDropdown
      onExcel={handleExcelExport}
      onPDF={handlePDFExport}
      disabled={isLoading || !hasGenerated}
    />
  );

  const toolbarFilters = (
    <>
      <div className="list-page-toolbar-filters-row">
        <div className="list-page-toolbar-slot">
          <div className="list-page-toolbar-filter">
            <Select
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              classNamePrefix="list-page-filter-select"
              components={reportMultiSelectComponents}
              styles={filterSelectStyles}
              options={companiesWithSelectAll}
              value={selectedCompany}
              onChange={(selected) => HandleSelect(
                selected,
                selectedCompany,
                setSelectedCompany,
                companies,
                companiesWithSelectAll,
              )}
              placeholder="Company"
            />
          </div>
        </div>
        <div className="list-page-toolbar-slot">
          <div className="list-page-toolbar-filter">
            <Select
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              classNamePrefix="list-page-filter-select"
              components={reportMultiSelectComponents}
              styles={filterSelectStyles}
              options={plantsWithSelectAll}
              value={selectedPlant}
              onChange={(selected) => HandleSelect(
                selected,
                selectedPlant,
                setSelectedPlant,
                plants,
                plantsWithSelectAll,
              )}
              placeholder="Plant"
            />
          </div>
        </div>
        <div className="list-page-toolbar-slot">
          <div className="list-page-toolbar-filter">
            <Select
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              classNamePrefix="list-page-filter-select"
              components={reportMultiSelectComponents}
              styles={filterSelectStyles}
              options={salesrepsWithSelectAll}
              value={selectedSalesrep}
              onChange={(selected) => HandleSelect(
                selected,
                selectedSalesrep,
                setSelectedSalesrep,
                salesreps,
                salesrepsWithSelectAll,
              )}
              placeholder="Marketing Person"
            />
          </div>
        </div>
        <div className="list-page-toolbar-slot">
          <div className="marketing-report-filter-field">
            <label className="marketing-report-filter-label" htmlFor="marketing-report-period">
              Report Period
            </label>
            <div className="list-page-toolbar-filter-date" id="marketing-report-period">
              <RangePicker
                value={[fromDate, toDate]}
                format="DD-MM-YYYY"
                allowClear={false}
                separator={<span className="marketing-report-date-separator">to</span>}
                placeholder={['From date', 'To date']}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                onChange={(values) => {
                  if (values?.[0] && values?.[1]) {
                    setFromDate(values[0]);
                    setToDate(values[1]);
                    if (!asOnDate || values[1].isAfter(asOnDate)) {
                      setAsOnDate(values[1]);
                    }
                  }
                }}
              />
            </div>
            <div className="marketing-report-selected-value">
              {fromDate.format('DD-MM-YYYY')} to {toDate.format('DD-MM-YYYY')}
            </div>
          </div>
        </div>
        <div className="list-page-toolbar-slot">
          <div className="marketing-report-filter-field">
            <label className="marketing-report-filter-label" htmlFor="marketing-report-as-on">
              Outstanding As On
            </label>
            <div className="list-page-toolbar-filter-date" id="marketing-report-as-on">
              <DatePicker
                value={asOnDate}
                format="DD-MM-YYYY"
                allowClear={false}
                placeholder="Select date"
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                onChange={(value) => {
                  if (value) {
                    setAsOnDate(value);
                  }
                }}
              />
            </div>
            <div className="marketing-report-selected-value">
              {asOnDate.format('DD-MM-YYYY')}
            </div>
          </div>
        </div>
      </div>
      <div className="list-page-toolbar-filters-row list-page-toolbar-filters-row-actions">
        {exportButtons}
        <Button
          color="primary"
          className="list-page-toolbar-generate-btn"
          onClick={loadReport}
          disabled={isLoading}
        >
          Generate
        </Button>
      </div>
    </>
  );

  return (
    <div id="outer-container" className="App">
      <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
      <div id="page-wrap" className="marketing-person-report-page">
        <div className="marketing-person-report-shell">
          <ListPageLayout
            moduleLabel="REPORTS"
            title="Marketing Person Report"
            subtitle="Marketing performance with existing customers, new customers, quotations, sales orders, sales, receipts, and outstanding details."
            modeLabel="REPORT"
            recordCount={activeSectionCount}
            recordLabel={activeSection.type === 'count' ? 'customers' : 'records'}
            searchText={searchText}
            onSearchChange={setSearchText}
            searchPlaceholder={`Search in ${activeSection.tabLabel}...`}
            toolbarFilters={toolbarFilters}
            toolbarVariant="stacked"
            reportListLink="/ReportAll"
          >
            {summaryCards.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.25,
                  mb: 2,
                  px: { xs: 0.5, md: 1 },
                }}
              >
                {summaryCards.map((card, index) => (
                  <Box
                    key={card.label}
                    sx={{
                      minWidth: 120,
                      borderRadius: '12px',
                      px: 1.75,
                      py: 1.1,
                      color: '#fff',
                      background: index % 2 === 0
                        ? 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #40B8E8 100%)'
                        : 'linear-gradient(135deg, #0E6A98 0%, #0090D8 55%, #40B8E8 100%)',
                      boxShadow: '0 8px 20px rgba(0, 144, 216, 0.18)',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9 }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2, mt: 0.25 }}>
                      {card.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {!hasGenerated && !isLoading && (
              <div className="marketing-report-placeholder">
                Select company, plant, marketing person, report period, and outstanding date. Then click Generate.
              </div>
            )}

            {hasGenerated && (
              <div className="marketing-report-context">
                <div className="marketing-report-context-item">
                  <span className="marketing-report-context-label">Report Period</span>
                  <span>{fromDate.format('DD-MM-YYYY')} to {toDate.format('DD-MM-YYYY')}</span>
                </div>
                <div className="marketing-report-context-item">
                  <span className="marketing-report-context-label">Outstanding As On</span>
                  <span>{asOnDate.format('DD-MM-YYYY')}</span>
                </div>
                <div className="marketing-report-context-item">
                  <span className="marketing-report-context-label">Marketing Person</span>
                  <span>{marketingPersonLabel}</span>
                </div>
              </div>
            )}

            {hasGenerated && (
              <div className="marketing-report-tabs" role="tablist" aria-label="Marketing report sections">
                {MARKETING_REPORT_SECTIONS.map((section) => {
                  const sectionCount = getMarketingSectionCount(section, filteredReportDetails);
                  const isActive = section.key === activeSectionKey;
                  return (
                    <button
                      key={section.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`marketing-report-tab${isActive ? ' is-active' : ''}`}
                      onClick={() => setActiveSectionKey(section.key)}
                    >
                      <span>{section.tabLabel}</span>
                      <span className="marketing-report-tab-count">{sectionCount}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {hasGenerated && (
              <MarketingPersonReportSection
                key={activeSection.key}
                title={activeSection.title}
                description={activeSection.description}
                type={activeSection.type}
                countValue={filteredReportDetails.existing_customer_count}
                columns={activeSection.columns}
                rows={activeSection.type === 'count' ? [] : (filteredReportDetails[activeSection.key] || [])}
                isLoading={isLoading}
              />
            )}
          </ListPageLayout>
        </div>
      </div>
    </div>
  );
}

export default MarketingPersonReport;
